import { memo, useMemo } from "react";
import { AlertCircle, Ban, PackageCheck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { Label } from "@/shared/components/ui/label";
import {
  formatCPF,
  formatCurrency,
  formatDateTime,
  formatPhone,
} from "@/shared/lib/formatters";
import { RENTAL_TYPE_LABELS } from "../RentalTypeBadge";
import { PaymentsField } from "../rental-create/PaymentsField";
import { PickupStockChecklist } from "./PickupStockChecklist";
import { PickupGownItemsList } from "./PickupGownItemsList";
import type {
  RentalPayment,
  RentalPaymentMethod,
  RentalResponseDTO,
} from "../../types/rental.types";

const PAYMENT_METHOD_LABEL: Record<RentalPaymentMethod, string> = {
  PIX: "PIX",
  CREDIT_CARD: "Crédito",
  DEBIT_CARD: "Débito",
  CASH: "Dinheiro",
};

interface RentalPickupDialogProps {
  open: boolean;
  rental: RentalResponseDTO | null;
  loading: boolean;
  selectedStockIds: number[];
  newPayments: RentalPayment[];
  invoiceObservation: string;
  onSelectedStockIdsChange: (ids: number[]) => void;
  onNewPaymentsChange: (payments: RentalPayment[]) => void;
  onInvoiceObservationChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
  onRequestCancel: () => void;
}

interface FieldProps {
  label: string;
  children: React.ReactNode;
}

const Field = memo(function Field({ label, children }: FieldProps) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="text-sm font-medium">{children}</div>
    </div>
  );
});

/**
 * Dialog principal do fluxo de retirada. Consolida a visualização do
 * aluguel em cards no padrão da tela de criação, o checklist de peças
 * (SUIT) ou a lista de itens de beca (GOWN), o `PaymentsField` para
 * registrar novos pagamentos e os botões "Cancelar aluguel"
 * (destructive) e "Confirmar retirada" (primary). Estado de seleção de
 * peças e dos novos pagamentos vive na página pai — o dialog é
 * controlado.
 *
 * O botão de confirmar fica desabilitado enquanto:
 * - SUIT: nem todas as peças estão marcadas
 * - Algum pagamento novo está com `paidValue <= 0`
 * - Loading ativo
 */
export const RentalPickupDialog = memo(function RentalPickupDialog({
  open,
  rental,
  loading,
  selectedStockIds,
  newPayments,
  invoiceObservation,
  onSelectedStockIdsChange,
  onNewPaymentsChange,
  onInvoiceObservationChange,
  onClose,
  onConfirm,
  onRequestCancel,
}: RentalPickupDialogProps) {
  const allStocksSelected = useMemo(() => {
    if (!rental || rental.rentalType !== "SUIT") return true;
    const items = rental.stockItems ?? [];
    if (items.length === 0) return false;
    return selectedStockIds.length === items.length;
  }, [rental, selectedStockIds]);

  const paymentsValid = useMemo(
    () => newPayments.every((p) => p.paidValue > 0),
    [newPayments],
  );

  const pendingValue = rental ? rental.totalValue - rental.paidValue : 0;
  const hasPending = pendingValue > 0.001;

  const handleToggleStock = (stockId: number) => {
    onSelectedStockIdsChange(
      selectedStockIds.includes(stockId)
        ? selectedStockIds.filter((id) => id !== stockId)
        : [...selectedStockIds, stockId],
    );
  };

  const handleScanResolved = (stockId: number) => {
    if (!selectedStockIds.includes(stockId)) {
      onSelectedStockIdsChange([...selectedStockIds, stockId]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(value) => !value && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle>
            {rental ? `Retirar Aluguel #${rental.id}` : "Retirada"}
          </DialogTitle>
          <DialogDescription>
            {rental
              ? `${rental.clientName} · Contrato ${rental.contractNumber}`
              : ""}
          </DialogDescription>
        </DialogHeader>

        {rental ? (
          <PickupBody
            rental={rental}
            selectedStockIds={selectedStockIds}
            newPayments={newPayments}
            invoiceObservation={invoiceObservation}
            hasPending={hasPending}
            pendingValue={pendingValue}
            onToggleStock={handleToggleStock}
            onScanResolved={handleScanResolved}
            onNewPaymentsChange={onNewPaymentsChange}
            onInvoiceObservationChange={onInvoiceObservationChange}
          />
        ) : null}

        <DialogFooter className="mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading}
            className="sm:mr-auto"
          >
            Fechar
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onRequestCancel}
            disabled={loading || !rental}
            className="gap-2"
          >
            <Ban className="w-4 h-4" /> Cancelar aluguel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={
              loading || !rental || !allStocksSelected || !paymentsValid
            }
            className="gap-2"
          >
            <PackageCheck className="w-4 h-4" />
            {loading ? "Retirando..." : "Confirmar retirada"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

interface PickupBodyProps {
  rental: RentalResponseDTO;
  selectedStockIds: number[];
  newPayments: RentalPayment[];
  invoiceObservation: string;
  hasPending: boolean;
  pendingValue: number;
  onToggleStock: (stockId: number) => void;
  onScanResolved: (stockId: number) => void;
  onNewPaymentsChange: (payments: RentalPayment[]) => void;
  onInvoiceObservationChange: (value: string) => void;
}

const PickupBody = memo(function PickupBody({
  rental,
  selectedStockIds,
  newPayments,
  invoiceObservation,
  hasPending,
  pendingValue,
  onToggleStock,
  onScanResolved,
  onNewPaymentsChange,
  onInvoiceObservationChange,
}: PickupBodyProps) {
  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Cliente, contrato e vendedor
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Cliente">{rental.clientName}</Field>
          <Field label="Documento">{formatCPF(rental.clientDocument)}</Field>
          <Field label="Telefone">{formatPhone(rental.clientPhone)}</Field>
          <Field label="Contrato">{rental.contractNumber}</Field>
          <Field label="Vendedor">{rental.sellerName}</Field>
          <Field label="Tipo">{RENTAL_TYPE_LABELS[rental.rentalType]}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Datas previstas</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Saída">{formatDateTime(rental.expectedOutAt)}</Field>
          <Field label="Retorno">{formatDateTime(rental.expectedInAt)}</Field>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Valores e pagamentos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Field label="Valor total">
              {formatCurrency(rental.totalValue)}
            </Field>
            <Field label="Valor pago">{formatCurrency(rental.paidValue)}</Field>
            <Field label="Pendente">
              <span className={hasPending ? "text-destructive" : undefined}>
                {formatCurrency(pendingValue)}
              </span>
            </Field>
          </div>

          {hasPending ? (
            <div className="flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/5 p-3 text-xs">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <p>
                Aluguel com pagamento pendente. Você pode confirmar a retirada
                mesmo assim ou registrar um novo pagamento abaixo.
              </p>
            </div>
          ) : null}

          {(rental.payments ?? []).length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Pagamentos existentes ({(rental.payments ?? []).length})
              </p>
              <ul className="space-y-1">
                {(rental.payments ?? []).map((payment, index) => (
                  <li
                    key={`${payment.paymentMethod}-${index}-${payment.paidValue}`}
                    className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <span className="flex-1">
                      {PAYMENT_METHOD_LABEL[payment.paymentMethod]}
                    </span>
                    <span className="text-muted-foreground">
                      {formatCurrency(payment.paidValue)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <PaymentsField
            payments={newPayments}
            onChange={onNewPaymentsChange}
          />

          <div className="space-y-2">
            <Label htmlFor="rental-pickup-invoice-observation">
              Observação da NF
            </Label>
            <Textarea
              id="rental-pickup-invoice-observation"
              placeholder="Descrição que aparece na nota fiscal"
              rows={3}
              maxLength={2000}
              value={invoiceObservation}
              onChange={(e) => onInvoiceObservationChange(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Opcional. Em branco usa o padrão da loja.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {rental.rentalType === "SUIT"
              ? "Peças do aluguel"
              : "Itens de beca"}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {rental.rentalType === "SUIT" ? (
            <PickupStockChecklist
              items={rental.stockItems ?? []}
              selectedStockIds={selectedStockIds}
              onToggle={onToggleStock}
              onScanResolved={onScanResolved}
            />
          ) : (
            <PickupGownItemsList items={rental.gownItems ?? []} />
          )}
        </CardContent>
      </Card>
    </div>
  );
});
